import { Injectable, computed, inject, signal } from '@angular/core';
import { VirtualFenceService } from '@vectronic/base-map';
import { dsDtos, dsEnums, webDtos } from '@vectronic/openApi-admin';
import { ScheduleHelp } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import { combineLatest, map } from 'rxjs';
import { AbstractScheduleService, ScheduleTypeConfig } from './abstract-schedule.service';
import { CameraScheduleService } from './camera-schedule.service';

export const DEFAULT_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  hasSkipCount: true,
  maxSkipCount: 95,
  hasFixRate: true,
  hasCommunicationInterval: false,
  hasEmptySchedule: false,
  isMandatory: false,
};

export const GPS_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
};

export const PROXIMITY_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
  hasEmptySchedule: true,
};

export const SOLEX_GPS_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
  maxSkipCount: 255,
};

export const EXTERNAL_SENSOR_RECEIVER_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
};

export const VIRTUAL_FENCE_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
  hasEmptySchedule: true,
};

export const COMMUNICATION_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
  hasCommunicationInterval: true,
  hasEmptySchedule: true,
  hasFixRate: false,
  hasSkipCount: false,
};

export const VHF_SCHEDULE_CONFIG: ScheduleTypeConfig = {
  ...DEFAULT_SCHEDULE_CONFIG,
  hasSkipCount: false,
  hasFixRate: false,
};

@Injectable({ providedIn: 'root' })
export class CommunicationScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = COMMUNICATION_SCHEDULE_CONFIG;
  override scheduleType = 'COMMUNICATION' as const;
}

@Injectable({ providedIn: 'root' })
export class BeaconScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = VHF_SCHEDULE_CONFIG;
  override scheduleType = 'VHF' as const;
}

@Injectable({ providedIn: 'root' })
export class ProximityScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = PROXIMITY_SCHEDULE_CONFIG;
  override scheduleType = 'PROXIMITY' as const;
}

@Injectable({ providedIn: 'root' })
export class GpsScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = GPS_SCHEDULE_CONFIG;
  override scheduleType = 'GPS' as const;
}

@Injectable({ providedIn: 'root' })
export class SolexGpsScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = SOLEX_GPS_SCHEDULE_CONFIG;
  override scheduleType = 'SOLEX_GPS' as const;
}

@Injectable({ providedIn: 'root' })
export class ExternalSensorScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = EXTERNAL_SENSOR_RECEIVER_SCHEDULE_CONFIG;
  override scheduleType = 'EXTERNAL_SENSOR' as any; //TODO type check not in enum => EXTERNAL_SENSOR_RECEIVER
}

@Injectable({ providedIn: 'root' })
export class VirtualFenceScheduleService extends AbstractScheduleService {
  override config: ScheduleTypeConfig = VIRTUAL_FENCE_SCHEDULE_CONFIG;
  override scheduleType = 'VIRTUAL_FENCE' as const;
}

@Injectable({ providedIn: 'root' })
export class ScheduleServices {
  COMMUNICATION = inject(CommunicationScheduleService);
  VHF = inject(BeaconScheduleService);
  PROXIMITY = inject(ProximityScheduleService);
  GPS = inject(GpsScheduleService);
  SOLEX_GPS = inject(SolexGpsScheduleService);
  EXTERNAL_SENSOR_RECEIVER = inject(ExternalSensorScheduleService);
  VIRTUAL_FENCE = inject(VirtualFenceScheduleService);
  VIRTUAL_FENCE_MAP = inject(VirtualFenceService);
  CAMERA = inject(CameraScheduleService);

  readonly defaultSchedules = signal<dsDtos.Schedule[]>([]);

  hasAnyEmptySchedule = computed(() =>
    ScheduleHelp.services().some((type) => this[type]?.isEmptyScheduleSignal()),
  );

  isFilled$ = combineLatest([
    ...ScheduleHelp.services().map((type) => this[type]?.isFilledAndChanged$),
    this.CAMERA.isFilledAndChanged$,
  ]);

  filledCountByKey$ = combineLatest([
    this.GPS.isFilledAndChanged$,
    this.COMMUNICATION.isFilledAndChanged$,
    this.VHF.isFilledAndChanged$,
    this.PROXIMITY.isFilledAndChanged$,
    this.SOLEX_GPS.isFilledAndChanged$,
    this.EXTERNAL_SENSOR_RECEIVER.isFilledAndChanged$,
    this.VIRTUAL_FENCE.isFilledAndChanged$,
    this.VIRTUAL_FENCE_MAP.isVirtualFenceChanged$,
    this.CAMERA.isFilledAndChanged$,
  ]).pipe(
    map(
      ([
        GPS,
        COMMUNICATION,
        VHF,
        PROXIMITY,
        SOLEX_GPS,
        EXTERNAL_SENSOR_RECEIVER,
        VIRTUAL_FENCE,
        VIRTUAL_FENCE_MAP,
        CAMERA,
      ]) => ({
        GPS: GPS ? 1 : undefined,
        COMMUNICATION: COMMUNICATION ? 1 : undefined,
        VHF: VHF ? 1 : undefined,
        PROXIMITY: PROXIMITY ? 1 : undefined,
        SOLEX_GPS: SOLEX_GPS ? 1 : undefined,
        EXTERNAL_SENSOR_RECEIVER: EXTERNAL_SENSOR_RECEIVER ? 1 : undefined,
        VIRTUAL_FENCE: VIRTUAL_FENCE ? 1 : undefined,
        VIRTUAL_FENCE_MAP: VIRTUAL_FENCE_MAP ? 1 : undefined,
        CAMERA: CAMERA ? 1 : undefined,
      }),
    ),
  );

  filledCount$ = this.isFilled$.pipe(
    map((filledSchedules) => filledSchedules.filter(Boolean).length || undefined),
  );

  initializeSchedules(
    schedules: dsDtos.Schedule[],
    updateIsFilled: boolean,
    ignoreUpdateDefault = false,
    isRemote = false,
  ): void {
    if (!ignoreUpdateDefault) {
      this.defaultSchedules.set(schedules || []);
    } else {
      this.defaultSchedules.set([]);
    }
    this.CAMERA.onRemoveSchedule();
    this.CAMERA.isRemote.set(isRemote);
    this.CAMERA.defaultRules$.next({ 0: {} });
    this.CAMERA.defaultRulesIndexes$.next([0]);
    ScheduleHelp.services().forEach((type) => {
      this[type].isRemote.set(isRemote);
      this[type].onRemoveSchedule();
      this[type].defaultRules$.next({ 0: {} });
      this[type].defaultRulesIndexes$.next([0]);
    });
    const newSchedules = this.filterSchedules(cloneDeep(schedules || []));
    newSchedules?.forEach(({ scheduleType, rules, cameraScheduleRules }) => {
      if (scheduleType === 'CAMERA') {
        this.initializeCameraSchedule(cameraScheduleRules, updateIsFilled, ignoreUpdateDefault);
      } else if (scheduleType) {
        this.initializeScheduleRules(scheduleType, rules, updateIsFilled, ignoreUpdateDefault);
      }
    });
  }

  private initializeCameraSchedule(
    cameraScheduleRules: dsDtos.CameraScheduleRule[],
    updateIsFilled: boolean,
    ignoreUpdateDefault: boolean,
  ): void {
    if (!ignoreUpdateDefault) {
      this.CAMERA.defaultRules$.next(cloneDeep(cameraScheduleRules));
      this.CAMERA.defaultRulesIndexes$.next(Array.from(cameraScheduleRules?.keys()));
    }
    this.CAMERA.rules$.next(cameraScheduleRules);
    this.CAMERA.rulesIndexes$.next(Array.from(cameraScheduleRules?.keys()));
    this.CAMERA.lastIndex = cameraScheduleRules?.length - 1;
    this.CAMERA.hasDefaultValue.set(true);
    for (const key in this.CAMERA.rules$.value) {
      this.CAMERA.isRuleExpanded[key] = true;
      if (updateIsFilled) {
        this.CAMERA.updateIsFilled(key as any, true);
      }
    }
  }

  private initializeScheduleRules(
    scheduleType: dsEnums.ScheduleType,
    rules: webDtos.ScheduleRule[],
    updateIsFilled: boolean,
    ignoreUpdateDefault: boolean,
  ): void {
    if (!ignoreUpdateDefault) {
      this[scheduleType].defaultRules$.next(rules as { [key: number]: webDtos.ScheduleRule });
      this[scheduleType].defaultRulesIndexes$.next(Array.from(rules?.keys()));
    }
    this[scheduleType].rules$.next(rules as { [key: number]: webDtos.ScheduleRule });
    this[scheduleType].rulesIndexes$.next(Array.from(rules?.keys()));
    this[scheduleType].lastIndex = rules?.length - 1;
    this[scheduleType].hasDefaultValue.set(true);
    for (const key in this[scheduleType].rules$.value) {
      this[scheduleType].isRuleExpanded[key] = true;
      if (updateIsFilled) {
        this[scheduleType].updateIsFilled(key as any, this[scheduleType].rules$.value[key], true);
      }
    }
    if (updateIsFilled && this[scheduleType].config.hasEmptySchedule && rules?.length === 0) {
      this[scheduleType].isFilledAndChanged.set(true);
    }
  }

  setSchedulesMandatory(scheduleTypes: dsEnums.ScheduleType[]) {
    ScheduleHelp.services().forEach((type) => {
      (this[type] as AbstractScheduleService).config.isMandatory = scheduleTypes.includes(type);
    });
  }

  resetSchedules(): void {
    this.defaultSchedules.set([]);
    this.CAMERA.onRemoveSchedule();
    this.CAMERA.removeDefaultSchedule();
    ScheduleHelp.services().map((type) => {
      this[type].onRemoveSchedule();
      this[type].removeDefaultSchedule();
    });
  }

  filterSchedules(schedules: dsDtos.Schedule[]): dsDtos.Schedule[] {
    const grouped = new Map<string, dsDtos.Schedule[]>();

    schedules.forEach((schedule) => {
      if (!grouped.has(schedule.scheduleType)) {
        grouped.set(schedule.scheduleType, []);
      }
      grouped.get(schedule.scheduleType).push(schedule);
    });

    return Array.from(grouped.values()).map((group) => {
      if (group.length === 1) {
        return group[0];
      }
      return group.find((obj) => obj.userDefined) || group[0];
    });
  }
}
